import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { majorMap, minorMap, certMap } from '../mapping';

const majorOptions = Array.from(majorMap.keys()).sort();
const minorOptions = Array.from(minorMap.keys()).sort();
const certificateOptions = Array.from(certMap.keys()).sort();

const AcademicProgramSelector = ({
  selectedMajors = [],
  selectedMinors = [],
  selectedCertificates = [],
  onMajorsChange,
  onMinorsChange,
  onCertificatesChange
}) => {
  const [inputValues, setInputValues] = useState({ majors: '', minors: '', certificates: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const wrapperRef = useRef(null);
  const inputRefs = {
    majors: useRef(null),
    minors: useRef(null),
    certificates: useRef(null)
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
        setActiveField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (type, value) => {
    setInputValues(prev => ({ ...prev, [type]: value }));
    setActiveField(type);

    let options;
    switch (type) {
      case 'majors':
        options = majorOptions.filter(opt => !selectedMajors.includes(opt));
        break;
      case 'minors':
        options = minorOptions.filter(opt => !selectedMinors.includes(opt));
        break;
      case 'certificates':
        options = certificateOptions.filter(opt => !selectedCertificates.includes(opt));
        break;
      default:
        options = [];
    }

    const filtered = value.trim() === '' ? [] : options.filter(option =>
      option.toLowerCase().includes(value.toLowerCase())
    );

    setSuggestions(filtered);
  };

  const handleSelect = (type, value) => {
    let selected;
    let onUpdate;

    switch (type) {
      case 'majors':
        selected = selectedMajors;
        onUpdate = onMajorsChange;
        break;
      case 'minors':
        selected = selectedMinors;
        onUpdate = onMinorsChange;
        break;
      case 'certificates':
        selected = selectedCertificates;
        onUpdate = onCertificatesChange;
        break;
      default:
        return;
    }

    if (selected.length < 2) {
      onUpdate([...selected, value]);
    }

    setInputValues(prev => ({ ...prev, [type]: '' }));
    setSuggestions([]);
    inputRefs[type].current?.focus();
  };

  const handleRemove = (type, valueToRemove) => {
    switch (type) {
      case 'majors':
        onMajorsChange(selectedMajors.filter(value => value !== valueToRemove));
        break;
      case 'minors':
        onMinorsChange(selectedMinors.filter(value => value !== valueToRemove));
        break;
      case 'certificates':
        onCertificatesChange(selectedCertificates.filter(value => value !== valueToRemove));
        break;
    }
  };

  const renderInputSection = (type, label, selected) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} (select up to 2)
      </label>
      <div className="relative">
        <div 
          className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
          onClick={() => inputRefs[type].current?.focus()}
        >
          {selected.map(value => (
            <span
              key={value}
              className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
            >
              {value}
              <button
                type="button"
                onClick={() => handleRemove(type, value)}
                className="hover:text-blue-600"
              >
                <X size={14} />
              </button>
            </span>
          ))}
          <input
            ref={inputRefs[type]}
            type="text"
            value={inputValues[type]}
            onChange={(e) => handleInputChange(type, e.target.value)}
            onFocus={() => setActiveField(type)}
            placeholder={selected.length === 0 ? `Search ${label.toLowerCase()}...` : ''}
            className="flex-1 outline-none min-w-[120px] text-sm"
            disabled={selected.length >= 2}
          />
        </div>

        {suggestions.length > 0 && activeField === type && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="max-h-60 overflow-y-auto">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => handleSelect(type, suggestion)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500">
        {selected.length}/2 selected
      </p>
    </div>
  );

  return (
    <div className="space-y-6" ref={wrapperRef}>

      {renderInputSection('majors', 'Majors', selectedMajors)}
      {renderInputSection('minors', 'Minors', selectedMinors)}
      {renderInputSection('certificates', 'Certificates', selectedCertificates)}
    </div>
  );
};

export default AcademicProgramSelector;